/** MediaMTX WHEP playback — sub-second live video without HLS segment delay. */

import { getSessionToken } from "../api";
import { logLiveTrace } from "./live-trace";

/**
 * Page origin for relative WHEP URLs. Production is whatever host served the
 * dashboard (dashcam.mylunago.com). Local dev stays on localhost. Never invent
 * a hostname — a hardcoded host fails DNS in the browser.
 */
export function whepPageOrigin(explicit?: string): string {
  const given = (explicit ?? "").trim();
  if (given) return given.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location && /^https?:$/i.test(window.location.protocol)) {
    return window.location.origin;
  }
  const fromEnv = typeof process !== "undefined" ? (process.env.PUBLIC_WEBRTC_ORIGIN ?? process.env.PUBLIC_ORIGIN ?? "") : "";
  return fromEnv.trim().replace(/\/$/, "") || "http://127.0.0.1";
}

export function whepUrl(playUrl: string): string {
  const trimmed = playUrl.replace(/\/+$/, "");
  if (!trimmed) return trimmed;
  if (/\/whep$/i.test(trimmed)) return trimmed;
  return `${trimmed}/whep`;
}

/**
 * MediaMTX returns the session path it saw (no /webrtc prefix, sometimes an
 * internal host). DELETE and ICE PATCH must stay on the same page URL as the
 * WHEP POST.
 */
export function whepSessionUrl(location: string, endpoint: string, origin = whepPageOrigin()): string {
  const base = new URL(endpoint, origin.endsWith("/") ? origin : `${origin}/`);
  const resolved = new URL(location, base);
  if (resolved.origin === base.origin && resolved.pathname.startsWith("/webrtc/")) return resolved.toString();
  const secret = resolved.pathname.split("/").filter(Boolean).pop() ?? "";
  if (!secret || secret.toLowerCase() === "whep") {
    if (resolved.origin !== base.origin) {
      return new URL(`${resolved.pathname}${resolved.search}`, base).toString();
    }
    return resolved.toString();
  }
  const path = `${base.pathname.replace(/\/+$/, "")}/${secret}`;
  return new URL(path, base).toString();
}

function hintLowDelay(receiver: RTCRtpReceiver): void {
  const ext = receiver as RTCRtpReceiver & {
    jitterBufferTarget?: number | null;
    playoutDelayHint?: number;
  };
  try {
    // 0 underruns on cellular jitter and the picture freezes. A few hundred
    // milliseconds absorbs that without a long delay.
    if ("jitterBufferTarget" in ext) ext.jitterBufferTarget = 0.4;
  } catch {
    /* unsupported */
  }
  try {
    if ("playoutDelayHint" in ext) ext.playoutDelayHint = 0.4;
  } catch {
    /* unsupported */
  }
}

export async function webrtcLatencySec(pc: RTCPeerConnection): Promise<number | null> {
  let jitter = 0;
  let decode = 0;
  let rtt = 0;
  let gotVideo = false;
  try {
    const stats = await pc.getStats();
    for (const row of stats.values()) {
      const rec = row as Record<string, number | string | undefined>;
      if (rec.type === "inbound-rtp" && rec.kind === "video") {
        gotVideo = true;
        const emitted = Number(rec.jitterBufferEmittedCount) || 0;
        const delay = Number(rec.jitterBufferDelay) || 0;
        if (emitted > 0) jitter = delay / emitted;
        const decoded = Number(rec.framesDecoded) || 0;
        const proc = Number(rec.totalDecodeTime ?? rec.totalProcessingDelay) || 0;
        if (decoded > 0 && proc > 0) decode = proc / decoded;
      }
      if (rec.type === "candidate-pair" && rec.state === "succeeded") {
        const pairRtt = Number(rec.currentRoundTripTime);
        if (Number.isFinite(pairRtt) && pairRtt > 0) rtt = pairRtt;
      }
    }
  } catch {
    return null;
  }
  if (!gotVideo) return null;
  const sec = jitter + decode + rtt / 2;
  if (!Number.isFinite(sec) || sec < 0 || sec > 30) return null;
  return sec;
}

export type WebrtcPlayback = {
  pc: RTCPeerConnection;
  stop: () => void;
  latencySec: () => Promise<number | null>;
};

/** MediaMTX WHEP waits for an unpublished path; nginx /webrtc/ read timeout is 3600s. Bound the handshake so HLS can start. */
export const WEBRTC_CONNECT_TIMEOUT_MS = 1800;

export async function startWebrtcPlayback(opts: {
  url: string;
  video: HTMLVideoElement;
  signal?: AbortSignal;
  connectTimeoutMs?: number;
  trace?: { vehicleId: string; channel: number };
}): Promise<WebrtcPlayback | null> {
  if (typeof RTCPeerConnection === "undefined") return null;
  const endpoint = whepUrl(opts.url);
  if (!endpoint) return null;
  const trace = opts.trace;
  logLiveTrace("whep_url", {
    vehicleId: trace?.vehicleId,
    channel: trace?.channel,
    step: "whep_url",
    url: endpoint,
  });
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    bundlePolicy: "max-bundle",
  });
  let sessionUrl = "";
  let stopped = false;
  const pendingIce: string[] = [];

  const pushIce = (candidate: string) => {
    if (stopped) return;
    const url = sessionUrl;
    if (!url) {
      pendingIce.push(candidate);
      return;
    }
    void fetch(url, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/trickle-ice-sdpfrag",
        ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}),
      },
      body: `a=${candidate}\r\n`,
    }).catch(() => undefined);
  };

  pc.onicecandidate = (ev) => {
    if (!ev.candidate?.candidate) return;
    pushIce(ev.candidate.candidate);
  };
  pc.onconnectionstatechange = () => {
    logLiveTrace("webrtc_connection", {
      vehicleId: trace?.vehicleId,
      channel: trace?.channel,
      step: "webrtc_connection",
      state: pc.connectionState,
      ice: pc.iceConnectionState,
    });
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    // DELETE while the session still exists. Closing the peer connection first
    // makes MediaMTX drop it, and the follow-up DELETE is a 404.
    const url = sessionUrl;
    sessionUrl = "";
    if (trace) {
      logLiveTrace("disconnect", {
        vehicleId: trace.vehicleId,
        channel: trace.channel,
        step: "disconnect",
        transport: "webrtc",
      });
    }
    if (url) {
      // 404 means MediaMTX already dropped the session. That must not surface as a player failure.
      void fetch(url, { method: "DELETE" })
        .then((res) => {
          if (res.status === 404) return;
        })
        .catch(() => undefined);
    }
    try {
      pc.getReceivers().forEach((r) => r.track?.stop());
    } catch {
      /* ignore */
    }
    try {
      pc.close();
    } catch {
      /* ignore */
    }
  };

  const connectAbort = new AbortController();
  const onParentAbort = () => connectAbort.abort();
  opts.signal?.addEventListener("abort", onParentAbort, { once: true });
  const timeout = setTimeout(
    () => connectAbort.abort(),
    opts.connectTimeoutMs ?? WEBRTC_CONNECT_TIMEOUT_MS,
  );
  const clearConnectTimer = () => {
    clearTimeout(timeout);
    opts.signal?.removeEventListener("abort", onParentAbort);
  };
  connectAbort.signal.addEventListener("abort", stop, { once: true });
  if (opts.signal?.aborted || connectAbort.signal.aborted) {
    clearConnectTimer();
    stop();
    return null;
  }
  const signal = connectAbort.signal;

  pc.addTransceiver("video", { direction: "recvonly" });
  pc.addTransceiver("audio", { direction: "recvonly" });
  pc.ontrack = (ev) => {
    hintLowDelay(ev.receiver);
    const stream = ev.streams[0] ?? new MediaStream([ev.track]);
    if (opts.video.srcObject !== stream) opts.video.srcObject = stream;
  };

  try {
    const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc, 400, signal);
    const sdp = pc.localDescription?.sdp;
    if (!sdp || stopped) {
      clearConnectTimer();
      stop();
      return null;
    }
    const res = await fetch(endpoint, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/sdp",
        Accept: "application/sdp",
        ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}),
      },
      body: sdp,
      signal,
    });
    if (!res.ok) {
      clearConnectTimer();
      stop();
      return null;
    }
    const loc = res.headers.get("Location") || res.headers.get("location") || "";
    if (loc) {
      try {
        sessionUrl = whepSessionUrl(loc, endpoint);
      } catch {
        sessionUrl = loc;
      }
      logLiveTrace("whep_session", {
        vehicleId: trace?.vehicleId,
        channel: trace?.channel,
        step: "whep_session",
        url: sessionUrl,
      });
      const queued = pendingIce.splice(0);
      for (const candidate of queued) pushIce(candidate);
    }
    const answer = await res.text();
    if (!answer || stopped) {
      clearConnectTimer();
      stop();
      return null;
    }
    await pc.setRemoteDescription({ type: "answer", sdp: answer });
    pc.getReceivers().forEach(hintLowDelay);
    opts.signal?.addEventListener("abort", stop, { once: true });
    clearConnectTimer();
    if (opts.signal?.aborted) {
      stop();
      return null;
    }
    return {
      pc,
      stop,
      latencySec: () => webrtcLatencySec(pc),
    };
  } catch {
    clearConnectTimer();
    stop();
    return null;
  }
}

function waitForIceGathering(pc: RTCPeerConnection, timeoutMs: number, signal?: AbortSignal): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", onState);
      signal?.removeEventListener("abort", onAbort);
      resolve();
    };
    const onState = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    const onAbort = () => done();
    const timer = window.setTimeout(done, timeoutMs);
    pc.addEventListener("icegatheringstatechange", onState);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** Resolves once a decoded frame is on the element, or false when the wait ends. */
export function waitForVideoFrame(video: HTMLVideoElement, signal: AbortSignal, timeoutMs: number): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  if (video.videoWidth > 0 && video.readyState >= 2) return Promise.resolve(true);
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      window.clearTimeout(timer);
      video.removeEventListener("loadeddata", onData);
      video.removeEventListener("playing", onData);
      signal.removeEventListener("abort", onAbort);
      resolve(ok);
    };
    const onData = () => {
      if (video.videoWidth > 0 && video.readyState >= 2) done(true);
    };
    const onAbort = () => done(false);
    const timer = window.setTimeout(() => done(video.videoWidth > 0 && video.readyState >= 2), timeoutMs);
    video.addEventListener("loadeddata", onData);
    video.addEventListener("playing", onData);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export function measuredHlsDelaySec(hls: { playingDate?: Date | null; latency?: number }, now = Date.now()): number | null {
  const pdt = hls.playingDate;
  if (pdt instanceof Date && !Number.isNaN(pdt.getTime())) {
    const lag = (now - pdt.getTime()) / 1000;
    if (Number.isFinite(lag) && lag >= 0 && lag < 30) return lag;
  }
  const lat = hls.latency;
  if (typeof lat === "number" && Number.isFinite(lat) && lat >= 0 && lat < 30) return lat;
  return null;
}
